import { nationalParcelCoverage, stateFipsToAbbr, stateName } from '@mollusk/shared';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import mapboxgl from 'mapbox-gl';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { api } from '../../api/endpoints';
import { LoadingPill } from '../../components/LoadingPill';
import { CountyDetailPanel } from './CountyDetailPanel';
import { CoverageHeadline } from './CoverageHeadline';
import { CoverageLegend } from './CoverageLegend';
import {
  byFeatureId,
  countiesInState,
  coverageByStateFips,
  featuresBounds,
  paintFeatures,
} from './geo';
import { EMPTY_FC, setSourceData } from './mapUtil';
import { topoToFeatures } from './topo';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN ?? '';

const US_CENTER: [number, number] = [-98, 38];
const US_ZOOM = 3;

const STATES_FILL = 'states-fill';
const STATES_LINE = 'states-line';
const COUNTIES_FILL = 'counties-fill';
const COUNTIES_LINE = 'counties-line';

// Mirrors the gate in apps/api/src/coverage/transform.ts
// (MIN_PARCELS_FOR_COVERAGE / MIN_PARCELS_PER_CAPITA) — update together.
const PARCEL_METHODOLOGY =
  'A county counts as covered only when we hold a real parcel dataset for it: at least 500 ' +
  'parcels and at least 1 parcel per 20 residents (this filters out a handful of border-sliver ' +
  'parcels spilling over from neighboring sources). The percentage is the population living in ' +
  'covered counties divided by the total population, using Census 2023 county estimates.';

/** US parcel-coverage choropleth: states by population-weighted coverage,
 *  drill into a state to see per-county coverage, click a county for detail. */
export function ParcelCoverageMap() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [ready, setReady] = useState(false);
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [activeCounty, setActiveCounty] = useState<string | null>(null);

  const summary = useQuery({ queryKey: ['coverage-parcels'], queryFn: api.coverageParcels });
  const statesGeo = useQuery({
    queryKey: ['coverage-geo', 'states'],
    queryFn: () => api.coverageGeo('states'),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const countiesGeo = useQuery({
    queryKey: ['coverage-geo', 'counties'],
    queryFn: () => api.coverageGeo('counties'),
    staleTime: Number.POSITIVE_INFINITY,
  });

  const stateFeatures = useMemo(
    () => (statesGeo.data ? topoToFeatures(statesGeo.data, 'states') : []),
    [statesGeo.data],
  );
  const countyFeatures = useMemo(
    () => (countiesGeo.data ? topoToFeatures(countiesGeo.data, 'counties') : []),
    [countiesGeo.data],
  );

  const statesFC = useMemo(() => {
    if (!summary.data || stateFeatures.length === 0) return null;
    return paintFeatures(stateFeatures, byFeatureId, coverageByStateFips(summary.data.states));
  }, [summary.data, stateFeatures]);

  const countyCoverage = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of summary.data?.counties ?? []) m.set(c.county_fips, 1);
    return m;
  }, [summary.data]);

  const countiesFC = useMemo(() => {
    if (!selectedState) return EMPTY_FC;
    return paintFeatures(
      countiesInState(countyFeatures, selectedState),
      byFeatureId,
      countyCoverage,
    );
  }, [selectedState, countyFeatures, countyCoverage]);

  const selectedStateRow = useMemo(() => {
    if (!selectedState || !summary.data) return null;
    const abbr = stateFipsToAbbr(selectedState);
    const row = summary.data.states.find((s) => s.state === abbr);
    return row ? { name: stateName(selectedState) ?? row.state, row } : null;
  }, [selectedState, summary.data]);

  const national = useMemo(
    () => (summary.data ? nationalParcelCoverage(summary.data.states) : null),
    [summary.data],
  );

  // Initialise the map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/mapbox/light-v11',
      center: US_CENTER,
      zoom: US_ZOOM,
    });
    mapRef.current = map;
    map.on('load', () => {
      map.addSource(STATES_FILL, { type: 'geojson', data: EMPTY_FC });
      map.addSource(COUNTIES_FILL, { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: STATES_FILL,
        type: 'fill',
        source: STATES_FILL,
        paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.75 },
      });
      map.addLayer({
        id: STATES_LINE,
        type: 'line',
        source: STATES_FILL,
        paint: { 'line-color': '#ffffff', 'line-width': 1 },
      });
      map.addLayer({
        id: COUNTIES_FILL,
        type: 'fill',
        source: COUNTIES_FILL,
        layout: { visibility: 'none' },
        paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.8 },
      });
      map.addLayer({
        id: COUNTIES_LINE,
        type: 'line',
        source: COUNTIES_FILL,
        layout: { visibility: 'none' },
        paint: { 'line-color': '#022b3a', 'line-width': 0.5, 'line-opacity': 0.4 },
      });

      map.on('click', STATES_FILL, (e) => {
        const id = e.features?.[0]?.id;
        if (id != null) setSelectedState(String(id));
      });
      map.on('click', COUNTIES_FILL, (e) => {
        const f = e.features?.[0];
        if (f?.properties?.covered && f.id != null) setActiveCounty(String(f.id));
      });
      for (const layer of [STATES_FILL, COUNTIES_FILL]) {
        map.on('mouseenter', layer, () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', layer, () => {
          map.getCanvas().style.cursor = '';
        });
      }
      setReady(true);
    });
    return () => {
      map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, []);

  // Push painted data into the sources.
  useEffect(() => {
    if (mapRef.current && statesFC) setSourceData(mapRef.current, STATES_FILL, statesFC);
  }, [statesFC]);
  useEffect(() => {
    if (mapRef.current) setSourceData(mapRef.current, COUNTIES_FILL, countiesFC);
  }, [countiesFC]);

  // Toggle drill-down view + fit bounds when a state is selected/cleared.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const drilled = selectedState !== null;
    const vis = (id: string, v: boolean) =>
      map.setLayoutProperty(id, 'visibility', v ? 'visible' : 'none');
    vis(COUNTIES_FILL, drilled);
    vis(COUNTIES_LINE, drilled);
    map.setPaintProperty(STATES_FILL, 'fill-opacity', drilled ? 0.08 : 0.75);
    if (drilled) {
      const bounds = featuresBounds(countiesFC.features);
      if (bounds) map.fitBounds(bounds, { padding: 40, duration: 600 });
    } else {
      map.flyTo({ center: US_CENTER, zoom: US_ZOOM, duration: 600 });
    }
  }, [selectedState, countiesFC, ready]);

  const error = summary.error ?? statesGeo.error ?? countiesGeo.error;
  const loading = summary.isFetching || statesGeo.isFetching || countiesGeo.isFetching;

  return (
    <Box>
      {error instanceof ApiError ? <Typography color="error">{error.message}</Typography> : null}
      <CoverageHeadline
        national={national}
        weightLabel="by population"
        recordNoun="parcels"
        methodology={PARCEL_METHODOLOGY}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, minHeight: 40 }}>
        {selectedState ? (
          <>
            <Button variant="outlined" size="small" onClick={() => setSelectedState(null)}>
              ← All states
            </Button>
            {selectedStateRow ? (
              <Typography variant="body2" color="text.secondary">
                {selectedStateRow.name}: {(selectedStateRow.row.coverage * 100).toFixed(0)}% of
                population covered · {selectedStateRow.row.covered_counties}/
                {selectedStateRow.row.total_counties} counties ·{' '}
                {selectedStateRow.row.parcel_count.toLocaleString()} parcels. Click a county for
                detail.
              </Typography>
            ) : null}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Shading = share of each state's population in counties we have parcels for. Click a
            state to drill into counties.
          </Typography>
        )}
      </Box>
      <Box sx={{ position: 'relative', width: '100%', height: 520 }}>
        <Box
          ref={containerRef}
          sx={{ position: 'absolute', inset: 0, borderRadius: 1, overflow: 'hidden' }}
        />
        <CoverageLegend unit="by population" />
        {loading ? (
          <LoadingPill
            label="Loading coverage…"
            sx={{ position: 'absolute', top: 12, right: 12 }}
          />
        ) : null}
      </Box>
      <CountyDetailPanel fips={activeCounty} onClose={() => setActiveCounty(null)} />
    </Box>
  );
}
